import _ from 'lodash'
import {DATA_TYPES} from '../../constants/constants'
import type {Pointer} from '@wix/document-services-types'
import type {CreateExtArgs, ExtensionAPI} from '@wix/document-manager-core'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {SchemaAPI, SchemaExtensionAPI} from '../schema/schema'
import {builderTypes, isBuilderData} from '../../utils/builderUtils'
import type {StructureExtensionAPI} from '../structure'

const DATA_TYPE = DATA_TYPES.data
const INNER_ELEMENTS_TYPE = DATA_TYPES.innerElements

const shouldTransform = (refTypes: readonly string[]) => refTypes.length === 1 && isBuilderData(refTypes[0])

const shouldTransformInnerElements = (refTypes: readonly string[], value: any) =>
    (value.innerElements && value.data) || value.innerElements || value.data

const getItemDataType = (type: string, schemaAPI: SchemaAPI) => {
    const compDef = schemaAPI.getDefinition(type)
    return (compDef.dataTypes || []).find((v: string) => v !== '')
}

const transformBuilderDataToFlatRecursively = (
    dataItem: any,
    type: string,
    schemaAPI: SchemaAPI,
    namespace: string = DATA_TYPE
): any => {
    schemaAPI.traverseReferences(
        dataItem,
        type,
        namespace,
        shouldTransform,
        (item: any, refTypes?: readonly string[]) => {
            if (item?.data && item.id) {
                item.data._id = item.id
            }
            return transformBuilderDataToFlatRecursively(item, refTypes![0], schemaAPI, namespace)
        }
    )

    return dataItem.data ?? dataItem
}

export const transformInnerElementsBuilderDataToFlat = (createExtArgs: CreateExtArgs, dataItem: any) => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI
    if (!dataItem?.innerElements) {
        return dataItem
    }

    schemaAPI.traverseReferences(
        dataItem,
        dataItem.type,
        INNER_ELEMENTS_TYPE,
        shouldTransformInnerElements,
        (item: any) => {
            if (item?.data?.type) {
                item.data = transformBuilderDataToFlatRecursively(item.data, item.data.type, schemaAPI, DATA_TYPE)
            }

            if (item.innerElements) {
                transformInnerElementsBuilderDataToFlat(createExtArgs, item.innerElements)
            }

            return item
        }
    )

    return dataItem
}

/**
 * Called before returning the result of data.get() and before returning serialized object from component.serialize()
 * Flattens nested builder data and changes every ids of items in an array to _id on data.get()
 * Transforms: {data: {prop1: 'test'}, type: "platform.builder.xxx.Data"}
 * Into: {prop1: 'test'}
 * For a more detailed explanation see https://wix.atlassian.net/browse/DM-12977
 */
export const transformBuilderDataToFlat = (
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer,
    dataItem: any,
    namespace: string = DATA_TYPE
) => {
    const {components, schemaAPI} = createExtArgs.extensionAPI as StructureExtensionAPI & SchemaExtensionAPI

    const compType = components.getComponentType(componentPointer)

    if (!builderTypes(schemaAPI, compType)) {
        return dataItem
    }

    return transformBuilderDataToFlatRecursively(dataItem, dataItem.type, schemaAPI, namespace)
}

export const transformBuilderDataToFlatClean = (extensionAPI: ExtensionAPI, compType: string, dataItem: any) => {
    const {schemaAPI} = extensionAPI as StructureExtensionAPI & SchemaExtensionAPI

    if (!schemaAPI.isSchemaCollectionRegistered(compType)) {
        return dataItem
    }

    return transformBuilderDataToFlatRecursively(dataItem, dataItem.type, schemaAPI)
}

const transformBuilderDataToNestedObjectsRecursively = (dataItem: any, type: any, schemaAPI: SchemaAPI): any => {
    const structuredItem: any = {data: dataItem, type}
    if (dataItem._id) {
        structuredItem.id = dataItem._id
        delete dataItem._id
    }

    if (dataItem.type === type) {
        delete dataItem.type
    }

    schemaAPI.traverseReferences(
        structuredItem,
        type,
        DATA_TYPE,
        shouldTransform,
        (item: any, refTypes?: readonly string[]) => {
            return transformBuilderDataToNestedObjectsRecursively(item, refTypes![0], schemaAPI)
        }
    )

    return structuredItem
}

/**
 * Called in data.update()
 * Nests the flat data inside a data property
 * Transforms: {prop1: 'test'}
 * Into: {data: {prop1: 'test'}, type: "platform.builder.xxx.Data"}
 * For a more detailed explanation see https://wix.atlassian.net/browse/DM-12977
 */
export const transformBuilderDataToNestedObjectsOnDataUpdate = (
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer,
    itemToSet: any,
    namespace: string = DATA_TYPE
) => {
    const {schemaAPI, dataModel, components} = createExtArgs.extensionAPI as SchemaExtensionAPI &
        DataModelExtensionAPI &
        StructureExtensionAPI

    const type = components.getComponentType(componentPointer) ?? itemToSet?.type

    if (!builderTypes(schemaAPI, type) && !isBuilderData(type)) {
        return itemToSet
    }

    const serializedItemInDal = dataModel.components.getItem(componentPointer, DATA_TYPE)
    const doesComponentHaveData = Boolean(serializedItemInDal?.id)
    let itemType: string = serializedItemInDal?.type ?? itemToSet.type
    if (!itemType) {
        itemType = getItemDataType(type, schemaAPI)
    }

    let flatData
    if (serializedItemInDal) {
        flatData = transformBuilderDataToFlatRecursively(serializedItemInDal, itemType, schemaAPI, namespace)
    } else {
        flatData = itemToSet
    }
    flatData = _.assign(flatData, itemToSet)
    itemToSet = transformBuilderDataToNestedObjectsRecursively(flatData, itemType, schemaAPI)
    if (doesComponentHaveData) {
        itemToSet.id = serializedItemInDal.id
    }
    return itemToSet
}

const transformInnerElementsBuilderDataToNestedObjects = (item: any, schemaAPI: SchemaAPI) => {
    if (!item?.innerElements) {
        return item
    }

    schemaAPI.traverseReferences(
        item,
        item.type,
        INNER_ELEMENTS_TYPE,
        shouldTransformInnerElements,
        (innerItem: any) => {
            if (innerItem?.data) {
                const type = getItemDataType(innerItem.referredType, schemaAPI)
                innerItem.data = transformBuilderDataToNestedObjectsRecursively(innerItem.data, type, schemaAPI)
            }

            if (innerItem.innerElements) {
                transformInnerElementsBuilderDataToNestedObjects(innerItem.innerElements, schemaAPI)
            }

            return innerItem
        }
    )

    return item
}

/**
 * Called in component.add() before deserializing
 * Nests the flat data inside a data property
 * Transforms: {prop1: 'test'}
 * Into: {data: data: {prop1: 'test'}, type: "platform.builder.xxx.Data"}
 * For a more detailed explanation see https://wix.atlassian.net/browse/DM-12977
 */
export const transformBuilderDataToNestedObjectsOnComponentAdd = (
    createExtArgs: CreateExtArgs,
    componentDefinition: any
) => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI

    const {componentType} = componentDefinition
    if (!builderTypes(schemaAPI, componentType)) {
        return
    }

    const itemType = getItemDataType(componentType, schemaAPI)

    if (componentDefinition.data) {
        componentDefinition.data = transformBuilderDataToNestedObjectsRecursively(
            componentDefinition.data,
            itemType,
            schemaAPI
        )
    }

    if (componentDefinition.innerElements) {
        componentDefinition.innerElements = transformInnerElementsBuilderDataToNestedObjects(
            componentDefinition.innerElements,
            schemaAPI
        )
    }

    return componentDefinition
}
